'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import type * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { cn } from '@/app/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const CalendarChevron: (props: {
  className?: string | undefined;
  size?: number | undefined;
  disabled?: boolean | undefined;
  orientation?: 'left' | 'right' | 'down' | 'up' | undefined;
}) => React.JSX.Element = ({ orientation, ...props }) => {
  if (orientation === 'left') {
    return <ChevronLeft size={16} strokeWidth={2} {...props} aria-hidden="true" />;
  }
  return <ChevronRight size={16} strokeWidth={2} {...props} aria-hidden="true" />;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  components: userComponents,
  ...props
}: CalendarProps) {
  const defaultClassNames = {
    months: 'relative flex sm:flex-row gap-4',
    month: 'w-full',
    month_caption: 'relative mx-10 mb-1 flex h-9 items-center justify-center z-20',
    caption_label: 'text-sm font-medium',
    nav: 'absolute top-0 flex w-full justify-between z-10',
    button_previous: 'h-9 text-system-white hover:text-system-white80 p-0',
    button_next: 'h-9 text-system-white hover:text-system-white80 p-0',
    weekday: 'size-9 p-0 text-xs font-medium text-system-white',
    day_button:
      'relative flex size-9 items-center border justify-center whitespace-nowrap rounded-lg p-0 text-system-white outline-offset-2 group-[[data-selected]:not(.range-middle)]:[transition-property:color,background-color,border-radius,box-shadow] group-[[data-selected]:not(.range-middle)]:duration-150 focus:outline-none group-data-[disabled]:pointer-events-none focus-visible:z-10 hover:border-system-blue02 border-transparent group-data-[selected]:bg-system-blue02 group-data-[disabled]:text-system-white24 group-data-[disabled]:line-through group-data-[outside]:text-system-white24 group-data-[outside]:group-data-[selected]:text-system-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 group-[.range-start:not(.range-end)]:rounded-e-none group-[.range-end:not(.range-start)]:rounded-s-none group-[.range-middle]:rounded-none group-data-[selected]:group-[.range-middle]:bg-system-white12 group-data-[selected]:group-[.range-middle]:text-system-white',
    day: 'group size-9 px-0 text-sm',
    range_start: 'range-start',
    range_end: 'range-end',
    range_middle: 'range-middle',
    today:
      '*:after:pointer-events-none *:after:absolute *:after:bottom-1 *:after:start-1/2 *:after:z-10 *:after:size-[3px] *:after:-translate-x-1/2 *:after:rounded-full *:after:bg-primary [&[data-selected]:not(.range-middle)>*]:after:bg-background [&[data-disabled]>*]:after:bg-system-white24 *:after:transition-colors',
    outside: 'text-system-white24 data-selected:bg-system-blue02 data-selected:text-system-white24',
    hidden: 'invisible',
    week_number: 'size-9 p-0 text-xs font-medium text-system-white',
  };

  const mergedClassNames: typeof defaultClassNames = Object.keys(defaultClassNames).reduce(
    (acc, key) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: <>
      ...acc,
      [key]: classNames?.[key as keyof typeof classNames]
        ? cn(defaultClassNames[key as keyof typeof defaultClassNames], classNames[key as keyof typeof classNames])
        : defaultClassNames[key as keyof typeof defaultClassNames],
    }),
    {} as typeof defaultClassNames,
  );

  const defaultComponents = {
    Chevron: CalendarChevron,
  };

  const mergedComponents = {
    ...defaultComponents,
    ...userComponents,
  };

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('w-fit', className)}
      classNames={mergedClassNames}
      components={mergedComponents}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
